import {
  Badge,
  Button,
  Combobox,
  DataTable,
  type DataTableColumn,
  Modal,
  Select,
} from "@etudescarre/ui";
import {
  EyeIcon,
  LockKeyIcon,
  LockKeyOpenIcon,
  PencilIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  AdminListPagination,
  AdminListSearch,
  useAdminListQuery,
} from "./components";
import { YardForm } from "./yard-form";

type Yard = {
  id: string;
  moaCompanyId: number | null;
  name: string;
  type: "lots_separes" | "entreprise_generale" | "conception_realisation";
  isLocked: boolean;
  createdAt: string;
  moaCompany: {
    id: number;
    name: string;
  } | null;
};

const yardTypeLabels = {
  lots_separes: "Lots séparés",
  entreprise_generale: "Entreprise générale",
  conception_realisation: "Conception-réalisation",
} as const;

export function YardsList() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingYard, setEditingYard] = useState<Yard | undefined>();
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [lockingYard, setLockingYard] = useState<Yard | null>(null);
  const [isLocking, setIsLocking] = useState(false);
  const [filterMoaCompany, setFilterMoaCompany] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);

  const fetcher = useCallback(
    async ({
      search,
      page,
      pageSize,
    }: {
      search?: string;
      page: number;
      pageSize: number;
    }) => {
      const { data, error } = await apiClient.GET("/api/admin/yards", {
        params: {
          query: {
            search: search ?? undefined,
            page,
            pageSize,
          },
        },
      });
      if (error) {
        throw new Error("Erreur lors du chargement des chantiers");
      }
      return {
        items: data.data.yards.map((y) => ({
          ...y,
          id: String(y.id),
        })),
        total: data.data.total,
      };
    },
    [],
  );

  const {
    items: allYards,
    total,
    loading,
    error,
    search,
    page,
    pageSize,
    setSearch,
    setPage,
    setPageSize,
    refetch,
  } = useAdminListQuery<Yard>(fetcher, "yards");

  // Client-side filters (MOA, type, status) applied post-fetch
  const filteredYards = useMemo(() => {
    return allYards.filter((yard) => {
      if (filterMoaCompany.length > 0) {
        if (
          !yard.moaCompany ||
          !filterMoaCompany.includes(String(yard.moaCompany.id))
        ) {
          return false;
        }
      }
      if (filterType.length > 0 && !filterType.includes(yard.type)) {
        return false;
      }
      if (filterStatus.length > 0) {
        const statusValue = yard.isLocked ? "locked" : "open";
        if (!filterStatus.includes(statusValue)) {
          return false;
        }
      }
      return true;
    });
  }, [allYards, filterMoaCompany, filterType, filterStatus]);

  const moaCompanyOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const yard of allYards) {
      if (yard.moaCompany) {
        unique.set(yard.moaCompany.id, yard.moaCompany.name);
      }
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: String(id) }));
  }, [allYards]);

  const typeOptions = [
    { label: "Lots séparés", value: "lots_separes" },
    { label: "Entreprise générale", value: "entreprise_generale" },
    { label: "Conception-réalisation", value: "conception_realisation" },
  ];

  const statusOptions = [
    { label: "Ouvert", value: "open" },
    { label: "Verrouillé", value: "locked" },
  ];

  const hasActiveClientFilters =
    filterMoaCompany.length > 0 ||
    filterType.length > 0 ||
    filterStatus.length > 0;

  const clearAllFilters = () => {
    setFilterMoaCompany([]);
    setFilterType([]);
    setFilterStatus([]);
  };

  const handleCreate = () => {
    setEditingYard(undefined);
    setDrawerOpen(true);
  };

  const handleEdit = (yard: Yard) => {
    setEditingYard(yard);
    setDrawerOpen(true);
  };

  const handleToggleLock = (yard: Yard) => {
    setLockingYard(yard);
    setLockModalOpen(true);
  };

  const confirmToggleLock = async () => {
    if (!lockingYard) return;

    setIsLocking(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/yards/{id}",
      {
        params: { path: { id: Number(lockingYard.id) } },
      },
    );

    if (apiError) {
      toaster.error({
        title: "Erreur",
        description:
          apiError.error ||
          `Échec du ${lockingYard.isLocked ? "déverrouillage" : "verrouillage"} du chantier`,
      });
      setIsLocking(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: `Chantier ${lockingYard.isLocked ? "déverrouillé" : "verrouillé"} avec succès`,
    });

    setLockModalOpen(false);
    setLockingYard(null);
    setIsLocking(false);
    refetch();
  };

  const handleFormSuccess = () => {
    refetch();
  };

  // Column widths per .agents/screen-column-widths.md (écran 901): Nom and
  // MOA share the free space; Type/Statut/date/Actions are fixed, sized on
  // their widest content.
  const columns: DataTableColumn<Yard>[] = [
    {
      id: "name",
      header: "Nom",
      accessor: "name",
      isSortable: true,
      loadingCell: "text",
      cellClassName: "truncate",
    },
    {
      id: "type",
      header: "Type",
      accessor: (row) => yardTypeLabels[row.type],
      isSortable: true,
      loadingCell: "text",
      headerClassName: "w-40",
    },
    {
      id: "moaCompany",
      header: "MOA",
      accessor: (row) => row.moaCompany?.name ?? "-",
      loadingCell: "text",
      cellClassName: "truncate",
    },
    {
      id: "status",
      header: "Statut",
      accessor: (row) => (row.isLocked ? "Verrouillé" : "Ouvert"),
      cell: ({ row }) => (
        <Badge variant={row.isLocked ? "red" : "green"}>
          {row.isLocked ? "Verrouillé" : "Ouvert"}
        </Badge>
      ),
      loadingCell: "badge",
      headerClassName: "w-28",
    },
    {
      id: "createdAt",
      header: "Créé le",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString("fr-FR"),
      loadingCell: "text",
      headerClassName: "w-28",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      headerClassName: "w-32",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            plain
            onClick={() => navigate(`/admin/yards/${row.id}`)}
            aria-label="Détails"
          >
            <EyeIcon className="size-4" />
          </Button>
          <Button plain onClick={() => handleEdit(row)} aria-label="Modifier">
            <PencilIcon className="size-4" />
          </Button>
          <Button
            plain
            onClick={() => handleToggleLock(row)}
            aria-label={row.isLocked ? "Déverrouiller" : "Verrouiller"}
          >
            {row.isLocked ? (
              <LockKeyOpenIcon className="size-4" />
            ) : (
              <LockKeyIcon className="size-4" />
            )}
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Chantiers
          </h2>
          <Button onClick={handleCreate}>
            <PlusIcon data-slot="icon" className="size-4" />
            Créer un chantier
          </Button>
        </div>

        <div className="mb-4 flex items-end gap-3">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <AdminListSearch
              value={search}
              onChange={setSearch}
              placeholder="Rechercher par nom..."
              label="Nom"
              id="yards-search"
            />
            <Combobox
              options={moaCompanyOptions}
              value={filterMoaCompany}
              onValueChange={({ value }) => setFilterMoaCompany(value)}
              placeholder="Tous les MOA"
              label="MOA"
            />
            <Select
              options={typeOptions}
              value={filterType}
              onValueChange={({ value }) => setFilterType(value)}
              placeholder="Tous les types"
              label="Type"
            />
            <Select
              options={statusOptions}
              value={filterStatus}
              onValueChange={({ value }) => setFilterStatus(value)}
              placeholder="Tous les statuts"
              label="Statut"
            />
          </div>
          <Button
            plain
            onClick={clearAllFilters}
            disabled={!hasActiveClientFilters}
            aria-label="Réinitialiser les filtres"
          >
            <XIcon data-slot="icon" />
            Réinitialiser
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={filteredYards}
          isLoading={loading}
          isEmpty={filteredYards.length === 0}
          emptyState={
            <div className="flex min-h-[200px] items-center justify-center text-gray-500">
              {search ? "Aucun résultat pour cette recherche" : "Aucune donnée"}
            </div>
          }
        />

        <AdminListPagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <YardForm
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        yard={
          editingYard
            ? {
                id: Number(editingYard.id),
                moaCompanyId: editingYard.moaCompanyId,
                name: editingYard.name,
                type: editingYard.type,
                localisationData: null,
                comissioningDate: null,
              }
            : undefined
        }
        onSuccess={handleFormSuccess}
      />

      <Modal.Root
        open={lockModalOpen}
        onOpenChange={(details) => setLockModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>
                  Confirmer le{" "}
                  {lockingYard?.isLocked ? "déverrouillage" : "verrouillage"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir{" "}
                  {lockingYard?.isLocked ? "déverrouiller" : "verrouiller"} le
                  chantier "{lockingYard?.name}" ?
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setLockModalOpen(false)}
                  disabled={isLocking}
                >
                  Annuler
                </Button>
                <Button onClick={confirmToggleLock} disabled={isLocking}>
                  {isLocking
                    ? "En cours..."
                    : lockingYard?.isLocked
                      ? "Déverrouiller"
                      : "Verrouiller"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}

export type YardsListProps = Record<string, never>;
